import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useImmer } from "use-immer";

import { IgButton, IgTypography } from "@intelligems/ig-theme";
import { ExperienceDto } from "@intelligems/ig-types";
import { Grid } from "@mui/material";

import { useConfiguration } from "../../api/configurations/use-configuration";
import { useGetExperiencesList } from "../../api/experiences-list/get-experiences-list";
import {
  getNewestEndedTest,
  getRunningExperiences,
} from "../../api/formatters/dashboard";
import { filterExperiences } from "../../api/formatters/homeDashboard";
import { useGetOrg } from "../../api/organizations/get-organization";
import { useLoggedInUser } from "../../api/user-auths/login";
import { TestIdeasAiButton } from "../../components/Buttons/TestIdeasAiButton";
import { CreateExperienceTestDialog } from "../../components/Dialogs/CreateExperienceTestDialog";
import { UserInfoPopup } from "../../components/Dialogs/UserInfoPopup";
import { Resources } from "../../components/MainApp/Utils/Resources";
import IgErrorBoundary from "../../components/Utils/IgErrorBoundary";
import { setDialog } from "../../store/slices/global";
import { IAppState } from "../../store/types/store";
import theme from "../../theme";
import Loading from "../../utils/Loading";

import { ActiveTestSection } from "./components/ActiveTest/ActiveTestSection";
import { GroupAnalysis } from "./components/GroupAnalysis/GroupAnalysis";
import { MetricHeader } from "./components/Metrics/MetricHeader";
import { OnboardingTestFlowHelper } from "./components/OnboardingTestFlowHelper";

export const HomeDashboard = () => {
  const dispatch = useDispatch();
  const organization = useGetOrg();
  const user = useLoggedInUser();
  const navigate = useNavigate();

  const selectedTestType = useConfiguration<string>("onboarding_test_flow");

  const experiencesList = useGetExperiencesList({ category: "experiment" });
  const campaigns = useGetExperiencesList({ category: "personalization" });

  const [impactExperiences, setImpactExperiences] = useImmer<ExperienceDto[]>(
    []
  );
  const [controlWinners, setControlWinners] = useImmer<ExperienceDto[]>([]);
  const [nonControlWinners, setNonControlWinners] = useImmer<ExperienceDto[]>(
    []
  );

  const shouldRenderOnboardingHelper = useMemo(() => {
    if (campaigns.isSuccess && experiencesList.isSuccess) {
      return (
        experiencesList.data.experiencesList.filter(
          (exp) => exp.status !== "pending"
        ).length === 0 &&
        campaigns.data.experiencesList.filter(
          (campaign) => campaign.startedAtTs
        ).length === 0
      );
    } else return false;
  }, [experiencesList, campaigns, selectedTestType, user]);

  useEffect(() => {
    if (experiencesList.isSuccess) {
      const filteredExperiments = experiencesList.data.experiencesList.filter(
        (exp) =>
          exp.endedAtTs &&
          exp.experienceAnalysis?.controlMonthly !== null &&
          !exp.experienceAnalysis?.excludeFromImpact
      );
      setImpactExperiences(
        filteredExperiments?.sort((a, b) => b.endedAtTs! - a.endedAtTs!)
      );
      setControlWinners(filterExperiences(filteredExperiments, "control"));
      setNonControlWinners(
        filterExperiences(filteredExperiments, "nonControl")
      );
    }
  }, [experiencesList.isSuccess]);

  if (experiencesList.isSuccess && user.isSuccess) {
    return (
      <IgErrorBoundary>
        <UserInfoPopup />
        <CreateExperienceTestDialog />
        <Grid container direction={"column"} rowSpacing={2}>
          <Grid
            container
            item
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            xs={8}
            flexWrap={"nowrap"}
          >
            <Grid container item>
              <Grid item>
                <IgTypography
                  sx={{
                    fontSize: "34px",
                    [theme.breakpoints.down("md")]: {
                      fontSize: "24px",
                    },
                    fontWeight: 600,
                  }}
                >
                  Welcome, {organization.data.org.name}!
                </IgTypography>
              </Grid>
            </Grid>
            <Grid container item columnSpacing={2} justifyContent={"end"}>
              <Grid item>
                <TestIdeasAiButton />
              </Grid>
              <Grid item>
                <IgButton
                  onClick={() => {
                    dispatch(
                      setDialog({ dialog: "createExperiment", open: true })
                    );
                  }}
                  variant={"contained"}
                >
                  Create New Test
                </IgButton>
              </Grid>
            </Grid>
          </Grid>

          {shouldRenderOnboardingHelper && (
            <Grid item>
              <OnboardingTestFlowHelper />
            </Grid>
          )}

          {impactExperiences.length > 0 && (
            <Grid container item columnSpacing={2} rowSpacing={3}>
              <Grid container item xs={12} sm={5}>
                <Grid item sx={{ width: "100%" }}>
                  <MetricHeader
                    nonControlWinners={nonControlWinners}
                    allImpactExperiments={impactExperiences}
                    allExperiments={experiencesList.data.experiencesList}
                    newestEndedTest={getNewestEndedTest(impactExperiences)!}
                  />
                </Grid>
              </Grid>
              <Grid container item xs={12} sm={7}>
                <GroupAnalysis
                  nonControlWinners={nonControlWinners}
                  controlWinners={controlWinners}
                  allImpactExperiments={impactExperiences}
                  impactMetric={
                    impactExperiences[0].experienceAnalysis.impactMetric!
                  }
                />
              </Grid>
            </Grid>
          )}
          {getRunningExperiences(experiencesList.data.experiencesList, false)
            .length > 0 && (
            <Grid item>
              <ActiveTestSection
                activeExperiences={getRunningExperiences(
                  experiencesList.data.experiencesList,
                  false
                )}
              />
            </Grid>
          )}
          <Grid item>
            <Resources />
          </Grid>
        </Grid>
      </IgErrorBoundary>
    );
  }
  return (
    <Grid container direction={"row"}>
      <Grid item>
        <Loading centered={true} />
      </Grid>
    </Grid>
  );
};
